// Preview-only listening comparison for the two ambient candidates.
// Reached only by direct URL: /audio-compare
// Not linked from navigation, not in the sitemap, and unpublished.
//
// This page exists purely so the editor can A/B the current Chapter 3 track
// (ambience.mp3) against the unreferenced monastery track (ambience-monastery.mp3)
// before any signature-track decision. Both files play through native browser
// audio controls — no site-level ambient audio, no Chapter 3 wiring changed.

import { useEffect } from "react";

export default function AudioCompare() {
  // Emit noindex,nofollow so this unlisted editorial page cannot be indexed
  // even if a search engine discovers the URL directly.
  useEffect(() => {
    let meta = document.head.querySelector('meta[name="robots"]');
    const created = !meta;
    if (created) { meta = document.createElement("meta"); meta.setAttribute("name", "robots"); document.head.appendChild(meta); }
    const prev = meta.getAttribute("content");
    meta.setAttribute("content", "noindex,nofollow");
    return () => {
      if (created) document.head.removeChild(meta);
      else if (prev) meta.setAttribute("content", prev);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] text-primary-custom" data-testid="audio-compare-page">
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-24">
        <span className="text-[10px] font-sans tracking-[0.4em] uppercase text-[var(--accent-bronze)] font-semibold block">
          Preview-only · A / B comparison
        </span>
        <h1 className="mt-6 font-serif tracking-tight text-3xl md:text-4xl leading-tight">
          Chapter 3 · candidate soundtrack comparison
        </h1>
        <p className="mt-4 font-serif text-base italic text-secondary-custom leading-relaxed max-w-2xl">
          Two ambient tracks are currently sitting in the audio library. Below they can be played independently.
          Neither is applied to Chapter 3 by this page — the Chapter 3 wiring remains exactly as approved.
        </p>

        <section className="mt-12 space-y-12">
          <article data-testid="audio-compare-a">
            <div className="flex items-baseline gap-3 mb-2">
              <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold">A</span>
              <h2 className="font-serif text-xl">ambience.mp3 <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-secondary-custom/70 ml-2">— currently wired to Chapter 3 &amp; the neutral ambient scene</span></h2>
            </div>
            <p className="font-sans text-[11px] tracking-wide text-secondary-custom mb-4">/audio/ambience.mp3 · 1.92 MB</p>
            <audio controls preload="none" src="/audio/ambience.mp3" data-testid="audio-compare-a-player" className="w-full" />
          </article>

          <article data-testid="audio-compare-b">
            <div className="flex items-baseline gap-3 mb-2">
              <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold">B</span>
              <h2 className="font-serif text-xl">ambience-monastery.mp3 <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-secondary-custom/70 ml-2">— present in /audio but not referenced by SCENE_SRC</span></h2>
            </div>
            <p className="font-sans text-[11px] tracking-wide text-secondary-custom mb-4">/audio/ambience-monastery.mp3 · 2.70 MB</p>
            <audio controls preload="none" src="/audio/ambience-monastery.mp3" data-testid="audio-compare-b-player" className="w-full" />
          </article>
        </section>

        <p className="mt-16 font-serif text-sm italic text-secondary-custom leading-relaxed">
          This page is unlisted, excluded from robots and the sitemap by its noindex meta, and is intended for
          editorial review only. It emits <code>meta name="robots" content="noindex,nofollow"</code> at mount.
        </p>
      </div>
    </div>
  );
}
